import { getJsonValueAtPath } from '@backstage/filter-predicates';

import { isFilled } from './depends-on';
import { isJsonValue } from './json-value';
import type { FieldValue, ReadField } from './match-value';

/**
 * A value that can be compared as one or more query terms.
 *
 * @remarks
 * An allowlist, not a denylist. `typeof value !== 'object'` would also admit
 * `function`, `symbol` and `bigint`, and it would admit `NaN` and the
 * infinities, which `String()` turns into the literal terms `"NaN"` and
 * `"Infinity"`. Naming the shapes that ARE comparable keeps a new `typeof`
 * result out by default.
 */
type Comparable = string | number | boolean | null;

function isComparable(value: unknown): value is Comparable {
  if (value === null) return true;
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return true;
    case 'number':
      // `NaN` and the infinities stringify to terms no backend field holds.
      return Number.isFinite(value);
    default:
      return false;
  }
}

/**
 * The field names the references inside these objects point at, in two sets.
 *
 * @remarks
 * A `ref` may sit in four shapes, and all four are reachable from `filter` and
 * `exclude` alike:
 *   1. as the whole value:            location: { ref: x }
 *   2. as an element of a list:       tags.t: [{ ref: x }, 'mdp']
 *   3. as an operator's operand:      tags.t: { has: { ref: x } }
 *   4. inside an operator's list:     tags.t: { has_any: [{ ref: x }] }
 *
 * Two sets rather than one, because they answer different questions. `all` is
 * the memo key: when an OPTIONAL reference's value arrives, the query still has
 * to be rebuilt, or it never starts filtering. `waitFor` is the derived
 * `dependsOn`, and an optional reference is absent from it.
 *
 * The parameter is `object` and not a filter type: finding references is a
 * walk over an unknown structure, so nothing here depends on which picker the
 * object came from. Anything that is not an object is left alone, which is why
 * a list of plain strings contributes no names.
 *
 * @param sides - The objects to search, each one skipped when `undefined`.
 * @returns `all`, every referenced field name, and `waitFor`, the subset that
 * has not opted out of waiting. Both are deduplicated and neither ever
 * contains a `path`.
 */
export function refFieldsOf(...sides: readonly (object | undefined)[]): {
  readonly all: readonly string[];
  readonly waitFor: readonly string[];
} {
  const all = new Set<string>();
  const waitFor = new Set<string>();

  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk); // shapes 2 and 4
      return;
    }
    if (typeof node !== 'object' || node === null) return; // a literal
    // A spread rather than a cast, for two reasons. It reads an unknown
    // object's members without asserting anything about its type, and it
    // copies OWN keys only. A `ref` inherited from a prototype is therefore
    // not a reference: the template author never wrote it, so it must not
    // become a dependency. Measured against `Object.create({ ref })`: the cast
    // reports that inherited name, the spread reports nothing.
    const record: Record<string, unknown> = { ...node };
    if (typeof record.ref === 'string') {
      all.add(record.ref); // shapes 1 and 3
      // Every reference waits for its field unless the author opted out.
      // `optional` says "do not wait", not "may not exist".
      if (record.optional !== true) waitFor.add(record.ref);
      return; // `path` is not a field name
    }
    Object.values(record).forEach(walk); // an operator object
  };

  for (const side of sides) {
    if (side === undefined) continue;
    // The whole side, not its members one by one: `walk` already descends
    // through any object, so naming `location` and `tags` here would only
    // repeat what the generic branch does.
    walk(side);
  }
  return { all: [...all], waitFor: [...waitFor] };
}

/**
 * How a field name that the form does not list should be read.
 *
 * @remarks
 * The picker has the `rootSchema` union and can tell a typo from an untouched
 * field, so there a missing name is an author error. The validator context has
 * no `registry`, so there it is indistinguishable from empty and must not
 * block a correct template on submit.
 */
export type UnknownFieldStance = 'author-error' | 'empty';

/**
 * Builds the reader that turns a referenced field name into its value.
 *
 * @param formData - Every field of the form, as the filler left it.
 * @param validKeys - The field names this form has.
 * @param onUnknownField - What a name outside `validKeys` means here.
 * @returns A reader that answers what one referenced field holds.
 */
export function makeReadField(
  formData: Readonly<Record<string, unknown>>,
  validKeys: ReadonlySet<string>,
  onUnknownField: UnknownFieldStance,
): ReadField {
  return (field, path): FieldValue => {
    if (!validKeys.has(field)) {
      return onUnknownField === 'empty'
        ? { kind: 'empty' }
        : { kind: 'unknown-field', known: [...validKeys].sort() }; // NOSONAR: sorts strings (field names); code-unit order is intended
    }

    const raw = formData[field];
    // A path only reaches into JSON, so anything else is treated as absent
    // rather than walked into.
    const walkable = isJsonValue(raw) ? raw : undefined;
    // A path narrows an object down to the part the author named. Without one,
    // the whole value is what gets compared.
    const value = path === undefined ? raw : getJsonValueAtPath(walkable, path);

    // Empty always drops this comparison, and a path that leads nowhere lands
    // here too.
    if (!isFilled(value)) return { kind: 'empty' };

    // Every ELEMENT has to be comparable, not just the container: a list of
    // objects would otherwise stringify to "[object Object]" and go into the
    // query as a real term.
    if (Array.isArray(value)) {
      return value.every(isComparable)
        ? { kind: 'value', values: value.map(String) }
        : { kind: 'object' };
    }

    if (!isComparable(value)) return { kind: 'object' };
    return { kind: 'value', values: [String(value)] };
  };
}
